import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Button, Center, Flex, Heading, Icon, SkeletonText, Spacer,
  useColorModeValue, Text
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { CgAttachment } from "react-icons/cg";
import { environments } from "constants/environments";
import { useAuth } from "contexts/auth";
import { IApiMetaData } from "interfaces/apiMetaData";
import { callApi } from "apiServices/callApi";
import ReactPlayer from 'react-player/lazy';
import { CalendarIcon } from "@chakra-ui/icons";
import { useParams } from "react-router-dom";
import { FaPlay } from "react-icons/fa";
import { BsFilePdfFill } from 'react-icons/bs';
import { RiFilePpt2Fill } from "react-icons/ri";
import { IoHeart, IoHeartOutline } from "react-icons/io5";
import { IBookData } from 'interfaces/bookData';
import DOMPurify from 'dompurify';  // Ensure DOMPurify is imported

// Safely create TrustedTypePolicy if it does not exist
const setupTrustedTypesPolicy = () => {
  if (window.trustedTypes) {
    try {
      // Try to create the policy; if it already exists, the catch block will handle the error
      window.trustedTypes.createPolicy('default', {
        createHTML: (input) => input, // Ensure this is safe
      });
    } catch (error) {
      // Handle the case where the policy already exists or cannot be created
      if (error instanceof DOMException && error.message.includes('Policy with name "default" already exists')) {
        // This error is expected if the policy is already created; we ignore it
        console.warn('TrustedTypePolicy "default" already exists.');
      } else {
        console.error('An unexpected error occurred while creating TrustedTypePolicy:', error);
      }
    }
  }
};

const sanitizeDescription = (html: string) => {
  // Setup Trusted Types policy safely
  setupTrustedTypesPolicy();

  return DOMPurify.sanitize(html);
};


const Book: React.FC = () => {
  const [state, setState] = useState<{
    isError: boolean;
    isLoading: boolean;
    like: boolean;
    data: IBookData | null;
    view: { name: string, url: string, type: string };
  }>({
    isError: false,
    isLoading: true,
    like: false,
    data: null,
    view: { name: "", url: "", type: "" },
  });

  const { id } = useParams<{ id: string }>();
  const cardMode = useColorModeValue('#fff', '#0b1437');
  const cardModeLoading = useColorModeValue('#fff', '#111c44');
  const textColor = useColorModeValue('secondaryGray.500', 'white');
  const { sharedData, setSharedData } = useAuth();

  const booksAPI = useMemo(() => environments.domain + sharedData?.apisList?.Item?.GetById, [sharedData]);
  const itemFavoritesAPI = useMemo(() => environments.domain + sharedData?.apisList.User.General, [sharedData]);

  useEffect(() => {
    if (sharedData?.user?.favorites?.includes(id)) {
      setState((prev) => ({ ...prev, like: true }));
    }
  }, [id, sharedData?.user?.favorites]);

  const fetchData = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const response: IApiMetaData = await callApi("GET", booksAPI, {}, {}, { id });
      if (response.Status === 200) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          data: response.Data.Content,
          view: {
            name: response.Data.Content.title,
            url: response.Data.Content.isExternal ? response.Data.Content.externalLink : response.Data.Content.doc,
            type: response.Data.Content.type
          }
        }));
      } else {
        setState((prev) => ({ ...prev, isError: true, isLoading: false }));
      }
    } catch (error) {
      setState((prev) => ({ ...prev, isError: true, isLoading: false }));
    }
  }, [booksAPI, id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFavorite = useCallback(async (itemId: string) => {
    const updatedLike = !state.like;
    setState((prev) => ({ ...prev, like: updatedLike }));
    try {
      const target = sharedData?.user?.favorites?.includes(itemId) ? "delete" : "add";
      const response: IApiMetaData = await callApi("PUT", itemFavoritesAPI, { favorites: itemId }, {}, { target }, false);

      if (response.Status === 200) {
        const updatedFavorites = target === "delete"
          ? sharedData?.user?.favorites?.filter((item: string) => item !== itemId)
          : [...sharedData?.user?.favorites, itemId];

        setSharedData((prev: any) => ({
          ...prev,
          user: {
            ...prev.user,
            favorites: updatedFavorites
          }
        }));
      }
    } catch (error) {
      console.error("Error updating favorites", error);
    }
  }, [sharedData, state.like, itemFavoritesAPI]);

  const renderAttachments = useMemo(() => (
    state.data?.attachments?.map((attachment: any) => (
      <Card key={attachment._id} className="hover-attachment" style={{ backgroundColor: '#dbdde3', color: 'white' }} onClick={() => setState((prev) => ({
        ...prev,
        view: { name: attachment.name, url: attachment.url, type: attachment.typeAttachment }
      }))}>
        <Flex minWidth='max-content' alignItems='center' gap='2'>
          <Box>
            <Heading size='md'>
              {attachment.typeAttachment === 'video' && <FaPlay size={25} color="#8f9bba" />}
              {attachment.typeAttachment === 'pdf' && <BsFilePdfFill size={25} color="#8f9bba" />}
              {attachment.typeAttachment === 'ppt' && <RiFilePpt2Fill size={25} color="#8f9bba" />}
              {!['video', 'pdf', 'ppt'].includes(attachment.typeAttachment) && <CgAttachment size={25} color="#8f9bba" />}
            </Heading>
          </Box>
          <Heading size='md' color='#090a52'>{attachment.name.charAt(0).toUpperCase() + attachment.name.slice(1)}</Heading>
          <Spacer />
        </Flex>
      </Card>
    ))
  ), [state.data]);

  if (state.isLoading) {
    return (
      <Box padding='6' boxShadow='lg' bg={cardModeLoading} rounded={'md'} h={"80vh"} w={"100%"} display="flex" justifyContent="center" alignItems="center">
        <Box w={"100%"}>
          <SkeletonText mt='4' noOfLines={1} spacing='4' skeletonHeight='300' w={"50%"} />
          <SkeletonText mt='4' noOfLines={10} spacing='4' skeletonHeight='2' />
        </Box>
      </Box>
    );
  }

  if (state.isError) {
    return <div>Error loading data.</div>;
  }

  const { data, view, like } = state;

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card>
        <Flex justify="space-between" align="flex-start" marginBottom="30px" flexWrap="wrap">
          <Box p='6' rounded='md' bg='white' className={`col-12 ${data?.attachments?.length === 0 ? 'lg:col-12' : 'lg:col-8'}`} style={{ background: cardMode }}>
            <Center h='50px' color='white' bg='#5296d8' fontSize='xl' fontWeight='bold' mb='20px'>
              {view.name.charAt(0).toUpperCase() + view.name.slice(1)}
            </Center>
            {view.type === 'video' ? (
              <ReactPlayer url={view.url} controls={true} width='100%' height={data?.attachments?.length === 0 ? '600px' : '500px'} />
            ) : (
              <iframe src={view.url} frameBorder="0" width='100%' height={data?.attachments?.length === 0 ? '600px' : '500px'} allowFullScreen></iframe>
            )}
          </Box>
          {data?.attachments && data.attachments.length > 0 && (
            <Box boxShadow='lg' p='6' rounded='md' bg='white' className="col-12 lg:col-3" style={{ background: cardMode }}>
              <Center h='50px' color='white' bg='#5296d8' fontSize='xl' fontWeight='bold' mb='20px'>Attachments</Center>
              {renderAttachments}
            </Box>
          )}
        </Flex>

        <div style={{ marginTop: "50px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <Text fontSize='3xl'>{data?.title}</Text>
              <Text fontSize='2xl' color={textColor}>{data?.supTitle}</Text>
            </div>
            <div>
              <Button
                bg='#dbdde3'
                _hover={{ bg: '#a5a7af' }}
                _active={{ bg: 'white' }}
                _focus={{ bg: 'white' }}
                p='0px !important'
                borderRadius='50%'
                minW='46px'
                h='46px'
                onClick={() => handleFavorite(id!)}>
                <Icon transition='0.2s linear' w='25px' h='25px' as={like ? IoHeart : IoHeartOutline} color='brand.500' />
              </Button>
            </div>
          </div>

          {/* Render sanitized description */}
          {data?.description && (
            <Text fontSize='lg' color={textColor} marginBlock="50px" textAlign="justify"
              dangerouslySetInnerHTML={{ __html: sanitizeDescription(data?.description || '') }}>
            </Text>
          ) }

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              {data?.keywords?.filter((keyword: string) => keyword !== 'undefined').map((keyword: string) => (
                <Text fontSize='md' color={textColor} fontWeight="bold" key={keyword} mr="10px">
                  #{keyword}
                </Text>
              ))}
            </div>
            <div>
              <Text fontSize='md' color={textColor}>
                {data?.createdAt ? new Date(data.createdAt).toDateString() : "Unknown Date"}
                <CalendarIcon color={textColor} verticalAlign="baseline" />
              </Text>
            </div>
          </div>
        </div>
      </Card>
    </Box>
  );
};

export default Book;
